/***
 *
 *   useAPI hook
 *   Make API calls and handle errors. Returns loading state and data.
 *
 *   DOCS
 *   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/hooks/useapi
 *
 *   method: get, post, put (string, required, default: get)
 *   trigger: change the trigger to re-start the call (boolean, optional)
 *   url: endpoint url (string, required)
 *
 **********/

import { useState, useEffect, useContext, useCallback, useRef } from "react";
import Axios from "axios";
import { ViewContext } from "components/lib";
import Settings from "settings.json";

// Mock data for development mode
function getMockData(url) {
  if (url.includes('/api/demo')) {
    return {
      stats: [
        { label: 'Maps Used', value: 1250, loading: false },
        { label: 'API Calls', value: 8500, loading: false },
        { label: 'Active Projects', value: 12, loading: false },
      ],
      revenue: { total: 2500, growth: 15.5 }
    };
  }
  
  if (url.includes('/api/account/users')) {
    return {
      users: [
        { id: 1, email: 'dev@example.com', name: 'Dev User', verified: true, avatar: null }
      ],
      invites: []
    };
  }
  
  if (url.includes('/api/key/scopes')) {
    return [
      "maps",
      "autocomplete", 
      "geocode",
      "directions",
      "map_matching",
      "optimize", 
      "matrix",
      "isochrone",
      "elevation",
      "navigation",
      "tiles",
      "staticImages"
    ];
  }
  
  if (url.includes('/api/key')) {
    return [
      {
        id: 1,
        name: 'Production API Key',
        key: 'mk_prod_****abcdef',
        allowed_websites: ['https://myapp.com', 'https://api.myapp.com'],
        active: true,
        created_at: '2024-01-15T10:30:00Z',
        last_used: '2025-01-13T09:15:00Z',
        usage_count: 8547
      },
      {
        id: 2,
        name: 'Development Key', 
        key: 'mk_dev_****fedcba',
        allowed_websites: ['http://localhost:3000', 'http://localhost:8080'],
        active: true,
        created_at: '2024-02-01T14:20:00Z',
        last_used: '2025-01-12T16:45:00Z',
        usage_count: 2156
      },
      {
        id: 3,
        name: 'Testing Environment',
        key: 'mk_test_****567890',
        allowed_websites: ['https://staging.myapp.com'],
        active: false,
        created_at: '2024-03-10T08:00:00Z',
        last_used: '2025-01-10T12:30:00Z',
        usage_count: 543
      }
    ];
  }
  
  if (url.includes('/api/stripe/usage')) {
    // Parse URL parameters to get API keys and meter type
    const urlParams = new URLSearchParams(url.split('?')[1]);
    const apiKeysParam = urlParams.get('apiKeysSet');
    const meter = urlParams.get('meter');
    const startDate = urlParams.get('startDate');
    const endDate = urlParams.get('endDate');
    
    // Generate realistic time labels based on date range
    const labels = [];
    let start, end;
    
    if (startDate && endDate) {
      start = new Date(startDate);
      end = new Date(endDate);
    } else {
      // Default to last 7 days if no date range specified
      const now = new Date();
      start = new Date(now - 6 * 24 * 60 * 60 * 1000);
      end = new Date();
    }
    
    // Generate labels for each day in the range
    const currentDate = new Date(start);
    while (currentDate <= end) {
      labels.push(currentDate.getTime());
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Define realistic usage patterns per API key
    const apiKeyProfiles = {
      '1': { // Production API Key
        name: 'Production API Key',
        baseMultiplier: 1.0,
        patterns: {
          maps: { base: 280, variance: 0.3, trend: 1.02 },
          search: { base: 450, variance: 0.25, trend: 1.01 },
          geocode: { base: 180, variance: 0.4, trend: 0.98 },
          directions: { base: 120, variance: 0.5, trend: 1.03 },
          map_matching: { base: 45, variance: 0.6, trend: 1.0 },
          optimize: { base: 25, variance: 0.7, trend: 1.05 },
          matrix: { base: 35, variance: 0.8, trend: 0.95 },
          isochrone: { base: 15, variance: 0.9, trend: 1.02 },
          elevation: { base: 65, variance: 0.4, trend: 1.01 },
          navigation: { base: 85, variance: 0.3, trend: 1.04 },
          tiles: { base: 1200, variance: 0.2, trend: 1.01 },
          static_images: { base: 95, variance: 0.5, trend: 0.99 }
        }
      },
      '2': { // Development Key
        name: 'Development Key',
        baseMultiplier: 0.3,
        patterns: {
          maps: { base: 85, variance: 0.8, trend: 1.05 },
          search: { base: 120, variance: 0.7, trend: 0.97 },
          geocode: { base: 65, variance: 0.9, trend: 1.02 },
          directions: { base: 35, variance: 1.2, trend: 1.08 },
          map_matching: { base: 12, variance: 1.5, trend: 0.95 },
          optimize: { base: 8, variance: 2.0, trend: 1.1 },
          matrix: { base: 15, variance: 1.3, trend: 0.92 },
          isochrone: { base: 5, variance: 2.5, trend: 1.15 },
          elevation: { base: 25, variance: 1.0, trend: 1.03 },
          navigation: { base: 18, variance: 1.4, trend: 1.07 },
          tiles: { base: 320, variance: 0.6, trend: 1.02 },
          static_images: { base: 28, variance: 1.1, trend: 0.98 }
        }
      },
      '3': { // Testing Environment
        name: 'Testing Environment',
        baseMultiplier: 0.08,
        patterns: {
          maps: { base: 22, variance: 1.5, trend: 0.95 },
          search: { base: 18, variance: 2.0, trend: 1.12 },
          geocode: { base: 12, variance: 2.2, trend: 0.88 },
          directions: { base: 8, variance: 2.5, trend: 1.2 },
          map_matching: { base: 3, variance: 3.0, trend: 0.8 },
          optimize: { base: 2, variance: 4.0, trend: 1.5 },
          matrix: { base: 4, variance: 2.8, trend: 0.7 },
          isochrone: { base: 1, variance: 5.0, trend: 2.0 },
          elevation: { base: 6, variance: 2.1, trend: 1.1 },
          navigation: { base: 3, variance: 3.5, trend: 0.9 },
          tiles: { base: 45, variance: 1.2, trend: 1.05 },
          static_images: { base: 7, variance: 2.3, trend: 0.85 }
        }
      }
    };

    // Determine which API keys to include
    let selectedApiKeys = ['1', '2', '3']; // Default to all keys
    if (apiKeysParam) {
      selectedApiKeys = apiKeysParam.split(',');
    }

    // Generate data for each selected API key
    const datasets = selectedApiKeys.map((keyId) => {
      const profile = apiKeyProfiles[keyId];
      if (!profile || !profile.patterns[meter]) {
        return null;
      }

      const pattern = profile.patterns[meter];
      const data = labels.map((timestamp, index) => {
        // Create realistic daily patterns with some randomness
        const baseValue = pattern.base * profile.baseMultiplier;
        const trendFactor = Math.pow(pattern.trend, index);
        const randomFactor = 1 + (Math.random() - 0.5) * pattern.variance;
        const weekendFactor = new Date(timestamp).getDay() % 6 === 0 ? 0.6 : 1.0; // Reduced weekend usage
        
        return Math.floor(baseValue * trendFactor * randomFactor * weekendFactor);
      });

      return {
        label: `${profile.name}`,
        data: data,
        borderColor: "rgba(91, 147, 255, 0.8)",
        backgroundColor: "rgba(91, 147, 255, 0.8)",
        barThickness: selectedApiKeys.length > 1 ? 12 : 18
      };
    }).filter(Boolean);

    // Calculate total usage across all datasets
    const totalUsage = datasets.reduce((total, dataset) => {
      return total + dataset.data.reduce((sum, value) => sum + value, 0);
    }, 0);

    return {
      usageHistory: {
        labels: labels,
        datasets: datasets
      },
      totalUsage: totalUsage
    };
  }
  
  if (url.includes('/api/stripe/subscription-details')) {
    return {
      subscribed_at: new Date().toISOString(),
      status: 'active'
    };
  }
  
  if (url.includes('/api/studio/styles/get-all')) {
    return {
      files: ['styles/map1', 'styles/map2']
    };
  }
  
  return null;
}

export function useAPI(url, method, trigger) {
  // wrap in useRef to prevent triggering useEffect multiple times
  const context = useRef(useContext(ViewContext));
  const [state, setState] = useState({ data: null, loading: false });

  const fetch = useCallback(async () => {
    try {
      if (!url) {
        setState({ data: null, loading: false });
        return false;
      }

      setState({ loading: true });
      
      // Check if using mock user token for dev mode
      const user = JSON.parse(localStorage.getItem("user"));
      if (user?.token === "mock-dev-token-123") {
        // Return mock data for development
        const mockData = getMockData(url);
        setState({ data: mockData, loading: false });
        return;
      }
      
      const res = await Axios({
        url: url,
        method: method || "get",
      });

      setState({ data: res.data.data, loading: false });
    } catch (err) {
      // If request fails and we have mock user, return mock data instead
      const user = JSON.parse(localStorage.getItem("user"));
      if (user?.token === "mock-dev-token-123") {
        const mockData = getMockData(url);
        setState({ data: mockData, loading: false });
        return;
      }

      // Silently handle JWT expiration errors - don't show popup
      const isJWTExpired = err?.response?.data?.message?.includes('jwt expired') ||
                          err?.response?.data?.error?.includes('jwt expired');

      if (!isJWTExpired && context?.current?.handleError) {
        context.current.handleError(err);
      } else if (!isJWTExpired) {
        console.error("API Error:", err);
      }
      setState({ data: null, loading: false });
    }
  }, [url, method, context, trigger]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return state;
}

export async function requestServer(endpoint, method, data) {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    let options = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + user?.token,
      },
    };

    if (method.toUpperCase() != "GET") {
      options.body = JSON.stringify(data);
    }

    // Same base URL resolution as Axios uses in app.jsx. This read
    // settings.json only, so it ignored VITE_API_URL and in production went to
    // portalapi.mapmetrics.org -- a bare CNAME with no Workers custom domain
    // and therefore no certificate, so every call failed at the TLS handshake.
    // requestServer backs the whole super admin dashboard, so that area was
    // unreachable in production while everything on Axios worked.
    const baseURL =
      import.meta.env.VITE_API_URL || Settings[process.env.NODE_ENV].server_url;
    const completeEndpointURL = baseURL + endpoint;

    const response = await fetch(completeEndpointURL, options);
    if (!response.ok) {
      // surface the status; the caller used to get a bare "not ok" for a 401,
      // a 403 and a 500 alike, and this function swallows throws
      throw new Error(`Request failed (${response.status}) for ${endpoint}`);
    }
    const responseData = await response.json(); // Handle response
    return responseData;
  } catch (error) {
    console.error("ERR: requestServer: ", error);
    throw error;
  }
}
